import { useCallback, useEffect, useRef, useState } from 'react';
import { FaExclamationTriangle, FaRedo, FaReply, FaTrash } from 'react-icons/fa';
import { MdCall, MdCallEnd, MdCallMissed } from 'react-icons/md';
import { useVoice } from '../hooks/useVoice';
import { KEY_SYNC_ERROR_MESSAGE, getKeySyncReasonMessage } from '../utils/e2eeKeySyncReasons';
import { formatClock, getMessageSenderName, isOwnMessage } from '../utils/formatters';
import { scheduleScrollToBottomIfElementBelow } from '../utils/MessengerScrollUtils';
import {
  MESSENGER_REACTION_TYPES,
  buildReactionTooltip,
  parseBooleanish,
  resolveMessageReactionEntries,
} from '../utils/ReactionUtils';

const getParticipantDisplayName = (participant) => {
  if (!participant || typeof participant !== 'object') return '';
  const user = participant?.user && typeof participant.user === 'object' ? participant.user : {};
  return (
    user?.name ||
    user?.username ||
    user?.email ||
    (participant?.user_id != null ? `User ${participant.user_id}` : '')
  );
};

const buildParticipantNameById = (participants = []) => {
  const byId = {};
  (Array.isArray(participants) ? participants : []).forEach((participant) => {
    const candidateIds = [participant?.user_id, participant?.user?.id]
      .filter((value) => value != null)
      .map((value) => `${value}`);
    if (!candidateIds.length) return;
    const name = getParticipantDisplayName(participant);
    candidateIds.forEach((id) => {
      byId[id] = name || byId[id] || `User ${id}`;
    });
  });
  return byId;
};

const resolveReactionHoverData = (entry, participantNameById, currentUserId) => {
  const names = [];
  const seen = new Set();
  const normalizedCurrentUserId = currentUserId == null ? null : `${currentUserId}`;
  const reactorIds = Array.isArray(entry?.reactorIds) ? entry.reactorIds : [];
  const pushName = (name) => {
    const normalized = `${name ?? ''}`.trim();
    if (!normalized || seen.has(normalized)) return;
    seen.add(normalized);
    names.push(normalized);
  };

  reactorIds.forEach((reactorId) => {
    const normalizedId = `${reactorId}`;
    if (normalizedCurrentUserId != null && normalizedId === normalizedCurrentUserId) {
      pushName('you');
      return;
    }
    pushName(participantNameById[normalizedId] || `User ${normalizedId}`);
  });

  if (entry?.reactedByCurrentUser) {
    pushName('you');
  }

  const totalCount = Math.max(0, Number(entry?.count) || 0);
  const unknownCount = Math.max(0, totalCount - names.length);
  return { names, unknownCount };
};

/**
 * Constructs a full URL for media files.
 * Handles dev mode proxy routing by replacing "/fusion" with the full base path.
 */
function getMediaUrl(relativeUrl) {
  if (!relativeUrl) return '';

  if (relativeUrl.startsWith('http://') || relativeUrl.startsWith('https://')) {
    return relativeUrl;
  }

  if (relativeUrl.startsWith('/')) {
    const baseUrl = process.env.PUBLIC_API_URL || '';

    if (baseUrl.startsWith('/')) {
      if (relativeUrl.startsWith('/fusion/')) {
        const pathAfterFusion = relativeUrl.substring('/fusion'.length);
        return `${baseUrl}${pathAfterFusion}`;
      }
      return relativeUrl;
    }

    try {
      const url = new URL(baseUrl);
      return `${url.origin}${relativeUrl}`;
    } catch (e) {
      return `${window.location.origin}${relativeUrl}`;
    }
  }

  return relativeUrl;
}

/**
 * Media attachment display (image or video).
 */
function MediaAttachment({ url, contentType, filename, onLoaded }) {
  const [loadState, setLoadState] = useState('loading');
  const fullUrl = getMediaUrl(url);

  useEffect(() => {
    if (!url) {
      setLoadState('error');
      return;
    }

    setLoadState('loading');
  }, [url, fullUrl]);

  const handleLoad = () => {
    setLoadState('loaded');
    onLoaded?.();
  };

  const handleError = (e) => {
    console.error('[MediaAttachment] Media load error:', e);
    console.error('[MediaAttachment] Failed URL:', fullUrl);
    setLoadState('error');
  };

  // Show loading spinner
  if (loadState === 'loading') {
    if (contentType === 'video') {
      return (
        <video
          className="responder-messenger-bubble__video"
          src={fullUrl}
          controls
          preload="metadata"
          onError={handleError}
          onLoadedMetadata={handleLoad}
        />
      );
    }

    return (
      <img
        className="responder-messenger-bubble__image"
        src={fullUrl}
        alt={filename || 'Attachment'}
        onLoad={handleLoad}
        onError={handleError}
      />
    );
  }

  // Show error state
  if (loadState === 'error') {
    return (
      <div className="responder-messenger-media-error">
        <FaExclamationTriangle size={16} />
        <span>Failed to load media</span>
      </div>
    );
  }

  // Show loaded media
  if (contentType === 'video') {
    return (
      <video
        className="responder-messenger-bubble__video"
        src={fullUrl}
        controls
        preload="metadata"
        onError={handleError}
        onLoadedMetadata={handleLoad}
      />
    );
  }

  return (
    <img
      className="responder-messenger-bubble__image"
      src={fullUrl}
      alt={filename || 'Attachment'}
      onLoad={handleLoad}
      onError={handleError}
    />
  );
}

function CallSystemMessage({ message, participants }) {
  const { availableCalls, callStatus, joinCall } = useVoice();
  const metadata = message.metadata || {};
  const status = metadata.status ?? '';
  const isStarted = status === 'initiated' || status === 'answered';
  const isMissed = status === 'missed';
  const roomId = metadata.roomId ?? metadata.room_id;
  const isJoinable =
    status === 'initiated' &&
    callStatus === 'idle' &&
    availableCalls.some((c) => c.roomId === roomId);

  const icon = isMissed ? (
    <MdCallMissed size={13} />
  ) : isStarted ? (
    <MdCall size={13} />
  ) : (
    <MdCallEnd size={13} />
  );

  return (
    <div className="responder-messenger-system-call">
      <span className="responder-messenger-system-call__icon">{icon}</span>
      <span className="responder-messenger-system-call__text">
        {isMissed
          ? `Missed voice call from ${getMessageSenderName(message, participants)}`
          : (message.content ?? '')}
      </span>
      {isJoinable && (
        <button
          type="button"
          className="responder-messenger-system-call__join"
          onClick={() => {
            const call = availableCalls.find((c) => c.roomId === roomId);
            if (call) joinCall(call.roomId, call.conversationId);
          }}
        >
          Join
        </button>
      )}
      <span className="responder-messenger-system-call__time">{formatClock(message.sent_at)}</span>
    </div>
  );
}


/**
 * Individual message bubble.
 */
function MessageBubble({
  message,
  currentUserId,
  participants,
  onDelete,
  onRetry,
  onReply,
  onToggleReaction,
  onJumpToMessage,
  onMediaLoaded,
}) {
  const isMine = isOwnMessage(message, currentUserId);
  const senderName = !isMine ? getMessageSenderName(message, participants) : null;
  const hasMedia = message.content_type === 'image' || message.content_type === 'video';
  const mediaUrl = message.metadata?.file?.url;
  const isOptimistic = message._optimistic || message._uploading;
  const isFailed = message._failed;
  const repliedToMessage = message.replied_to_message;
  const displayContent =
    typeof message.content === 'string' && message.content.length
      ? message.content
      : message.__e2ee?.status === 'pending-key'
        ? 'Waiting for encryption key...'
        : message.__e2ee?.status === 'failed'
          ? 'Unable to decrypt message'
          : '';
  const reactionEntries = resolveMessageReactionEntries(message, {
    currentUserId,
    parseOwnFlag: parseBooleanish,
  });
  const participantNameById = buildParticipantNameById(participants);
  const reactionCountByType = reactionEntries.reduce((acc, entry) => {
    if (entry?.type) acc[entry.type] = entry;
    return acc;
  }, {});
  const [hideReactionPicker, setHideReactionPicker] = useState(false);
  const pickerResetTimeoutRef = useRef(null);
  const canMutateReactions = !isOptimistic && !isFailed && typeof onToggleReaction === 'function';

  useEffect(() => {
    return () => {
      if (pickerResetTimeoutRef.current) {
        clearTimeout(pickerResetTimeoutRef.current);
      }
    };
  }, []);

  const handleToggleReaction = (reactionType) => {
    if (!canMutateReactions) return;
    setHideReactionPicker(true);
    if (pickerResetTimeoutRef.current) {
      clearTimeout(pickerResetTimeoutRef.current);
    }
    // Fallback for touch devices without reliable hover exit.
    pickerResetTimeoutRef.current = setTimeout(() => {
      setHideReactionPicker(false);
      pickerResetTimeoutRef.current = null;
    }, 1200);
    onToggleReaction(message.id, reactionType);
  };

  return (
    <div
      className={`responder-messenger-bubble-row${isMine ? ' is-mine' : ' is-received'}`}
      data-direction={isMine ? 'sent' : 'received'}
      data-message-id={message.id}
      onMouseLeave={() => setHideReactionPicker(false)}
    >
      <div className="responder-messenger-bubble">
        {!isMine && senderName && (
          <div className="responder-messenger-bubble__author">{senderName}</div>
        )}

        {repliedToMessage && (
          <div
            className="responder-messenger-bubble__replied-to"
            onClick={() => onJumpToMessage && onJumpToMessage(repliedToMessage.id)}
            role="button"
            tabIndex={0}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                onJumpToMessage && onJumpToMessage(repliedToMessage.id);
              }
            }}
          >
            <div className="responder-messenger-bubble__replied-to-bar" />
            <div className="responder-messenger-bubble__replied-to-content">
              <span className="responder-messenger-bubble__replied-to-label">
                {getMessageSenderName(repliedToMessage, participants) || 'Someone'}
              </span>
              <span className="responder-messenger-bubble__replied-to-text">
                {repliedToMessage.content_type === 'text'
                  ? repliedToMessage.content
                  : `[${repliedToMessage.content_type}]`}
              </span>
            </div>
          </div>
        )}

        {hasMedia && (
          <>
            {isFailed ? (
              <div className="responder-messenger-upload-failed">
                <FaExclamationTriangle size={13} />
                <span>Upload failed</span>
                {onRetry && (
                  <button
                    type="button"
                    className="responder-messenger-media-retry-btn"
                    onClick={() => onRetry(message.id)}
                  >
                    <FaRedo size={10} /> Retry
                  </button>
                )}
              </div>
            ) : (
              <div className="responder-messenger-media-wrapper">
                <MediaAttachment
                  url={mediaUrl}
                  contentType={message.content_type}
                  filename={message.metadata?.file?.filename}
                  onLoaded={() => onMediaLoaded?.(message.id)}
                />
              </div>
            )}
          </>
        )}

        {displayContent && (
          <div className="responder-messenger-bubble__text">{displayContent}</div>
        )}

        {canMutateReactions && !hideReactionPicker && (
          <div className="responder-messenger-reaction-picker" aria-label="Message reactions">
            {MESSENGER_REACTION_TYPES.map((reactionType) => {
              const existingEntry = reactionCountByType[reactionType];
              const isActive = existingEntry?.reactedByCurrentUser === true;
              return (
                <button
                  key={reactionType}
                  type="button"
                  className={`responder-messenger-reaction-picker__btn${isActive ? ' is-active' : ''}`}
                  onClick={() => handleToggleReaction(reactionType)}
                  aria-pressed={isActive}
                  aria-label={
                    isActive ? `Remove ${reactionType} reaction` : `Add ${reactionType} reaction`
                  }
                >
                  {reactionType}
                </button>
              );
            })}
          </div>
        )}

        {reactionEntries.length > 0 && (
          <div className="responder-messenger-reaction-summary">
            {reactionEntries.map((entry) => {
              const { names, unknownCount } = resolveReactionHoverData(
                entry,
                participantNameById,
                currentUserId,
              );
              const tooltipText = buildReactionTooltip({ ...entry, reactorNames: names });
              return (
                <button
                  key={entry.type}
                  type="button"
                  className={`responder-messenger-reaction-summary__chip${
                    entry?.reactedByCurrentUser ? ' is-own' : ''
                  }`}
                  onClick={() => handleToggleReaction(entry.type)}
                  title={tooltipText}
                  aria-label={`${entry.type} ${entry.count} reactions`}
                >
                  <span>{entry.type}</span>
                  <span>{entry.count}</span>
                  <div className="responder-messenger-reaction-summary__tooltip" role="tooltip">
                    {names.length > 0 ? (
                      names.map((name, index) => (
                        <div
                          key={`${entry.type}:${name}:${index}`}
                          className="responder-messenger-reaction-summary__tooltip-row"
                        >
                          {name}
                        </div>
                      ))
                    ) : (
                      <div className="responder-messenger-reaction-summary__tooltip-row">
                        No user details
                      </div>
                    )}
                    {unknownCount > 0 && (
                      <div className="responder-messenger-reaction-summary__tooltip-row is-muted">
                        +{unknownCount} more
                      </div>
                    )}
                  </div>
                </button>
              );
            })}
          </div>
        )}

        <div className="responder-messenger-bubble__time">
          {formatClock(message.sent_at)}
          {isOptimistic && ' • Sending...'}
        </div>

        {!isOptimistic && onReply && (
          <button
            type="button"
            className={`responder-messenger-bubble-reply-btn${!isMine ? ' is-received' : ''}`}
            aria-label="Reply to message"
            onClick={() => onReply(message)}
          >
            <FaReply size={9} />
          </button>
        )}

        {isMine && !isOptimistic && onDelete && (
          <button
            type="button"
            className="responder-messenger-bubble-delete-btn"
            aria-label="Delete message"
            onClick={() => onDelete(message.id)}
          >
            <FaTrash size={9} />
          </button>
        )}
      </div>
    </div>
  );
}

/**
 * Thread of messages with auto-scroll.
 * @param {{ messages: Array, currentUserId: number, participants: Array, onDeleteMessage: (id) => void, onReply: (msg) => void }} props
 */
export function MessageThread({
  messages,
  currentUserId,
  participants,
  conversationId,
  onDeleteMessage,
  onToggleReaction,
  onReply,
  loadMessages,
  loadMessageContext,

  loading = false, //kept from merge conflict - wes
  showEncryptionKeySyncBanner = false,
  isEncryptionKeySynced = false,
  keySyncStatus = null,
  keySyncReason = null, //kept from merge conflict - wes

  onRequestKeySync,
  onClearEncryptionKey,
}) {
  const scrollRef = useRef(null);
  const lastMessageId = messages[messages.length - 1]?.id;
  const [loadingMessageId, setLoadingMessageId] = useState(null);
  const [loadingOlder, setLoadingOlder] = useState(false);
  const isLoadingRef = useRef(false);
  const hasMoreOlderRef = useRef(true);
  const previousConversationIdRef = useRef(conversationId);
  const previousMessageCountRef = useRef(0);
  const previousLastMessageIdRef = useRef(null);

  const scheduleBottomScroll = useCallback(() => {
    const container = scrollRef.current;
    if (!container) return null;
    const targetElement = container.lastElementChild;
    if (!targetElement) return null;
    return scheduleScrollToBottomIfElementBelow(container, targetElement, { padding: 0 });
  }, []);

  // Reset "has more" flag when conversation changes
  useEffect(() => {
    if (conversationId !== previousConversationIdRef.current) {
      hasMoreOlderRef.current = true;
      previousMessageCountRef.current = 0;
      previousLastMessageIdRef.current = null;
      previousConversationIdRef.current = conversationId;
    }
  }, [conversationId]);

  const handleMediaLoaded = useCallback(
    (messageId) => {
      if (loadingMessageId || loadingOlder) return;
      if (`${messageId}` !== `${lastMessageId}`) return;
      scheduleBottomScroll();
    },
    [lastMessageId, loadingMessageId, loadingOlder, scheduleBottomScroll],
  );

  // Handler for jumping to a message (with context loading if needed)
  const handleJumpToMessage = async (messageId) => {
    const targetElement = document.querySelector(`[data-message-id="${messageId}"]`);

    if (targetElement) {
      // Message already loaded - scroll immediately
      targetElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
      targetElement.classList.add('responder-messenger-bubble-row--highlighted');
      setTimeout(() => {
        targetElement.classList.remove('responder-messenger-bubble-row--highlighted');
      }, 2000);
    } else if (loadMessageContext) {
      // Message not loaded - fetch context
      setLoadingMessageId(messageId);

      try {
        const result = await loadMessageContext(messageId, {
          contextBefore: 20,
          contextAfter: 20,
        });

        if (!result) {
          console.error('[MessageThread] Context loading returned null');
          setLoadingMessageId(null);
          return;
        }

        // Wait for React to re-render with new messages, then scroll
        // Use multiple intervals to keep checking for the message
        let attempts = 0;
        const maxAttempts = 10;
        const checkInterval = 100;

        const checkAndScroll = () => {
          const el = document.querySelector(`[data-message-id="${messageId}"]`);
          attempts++;

          if (el) {
            el.scrollIntoView({ behavior: 'smooth', block: 'center' });
            el.classList.add('responder-messenger-bubble-row--highlighted');
            setTimeout(() => {
              el.classList.remove('responder-messenger-bubble-row--highlighted');
            }, 2000);
            setLoadingMessageId(null);
          } else if (attempts < maxAttempts) {
            setTimeout(checkAndScroll, checkInterval);
          } else {
            setLoadingMessageId(null);
          }
        };

        // Start checking after a short delay to allow React to start rendering
        setTimeout(checkAndScroll, 100);
      } catch (err) {
        console.error('[MessageThread] Failed to load message context:', err);
        setLoadingMessageId(null);
      }
    }
  };

  // Infinite scroll: Load older messages when scrolling near top
  useEffect(() => {
    const container = scrollRef.current;
    if (!container || !loadMessages || !conversationId) return;

    const handleScroll = async () => {
      // Check if near top (within 200px) and we haven't reached the beginning
      if (
        container.scrollTop < 200 &&
        !isLoadingRef.current &&
        messages.length > 0 &&
        hasMoreOlderRef.current
      ) {
        isLoadingRef.current = true;
        setLoadingOlder(true);

        const oldestMessage = messages[0];
        const previousScrollHeight = container.scrollHeight;
        const previousScrollTop = container.scrollTop;

        try {
          const loadedMessages = await loadMessages(conversationId, {
            before: oldestMessage.sent_at,
            limit: 50,
          });

          // If API returned fewer than limit, we've reached the beginning
          if (loadedMessages.length < 50) {
            hasMoreOlderRef.current = false;
          }

          if (loadedMessages.length > 0) {
            // Maintain scroll position after loading older messages
            // Use requestAnimationFrame for better timing
            requestAnimationFrame(() => {
              requestAnimationFrame(() => {
                const newScrollHeight = container.scrollHeight;
                const heightDiff = newScrollHeight - previousScrollHeight;
                if (heightDiff > 0) {
                  container.scrollTop = previousScrollTop + heightDiff;
                }
              });
            });
          }
        } catch (err) {
          console.error('[MessageThread] Failed to load older messages:', err);
        } finally {
          // Add delay before allowing next load to prevent rapid-fire requests
          setTimeout(() => {
            setLoadingOlder(false);
            isLoadingRef.current = false;
          }, 300);
        }
      }
    };

    container.addEventListener('scroll', handleScroll);
    return () => container.removeEventListener('scroll', handleScroll);
  }, [messages, loadMessages, conversationId]);

  // Auto-scroll to bottom when new messages arrive
  // Skip auto-scroll if we're loading context or loading older messages
  useEffect(() => {
    const currentMessageCount = messages.length;
    const previousCount = previousMessageCountRef.current;
    const previousLastMessageId = previousLastMessageIdRef.current;

    // Initial load: always scroll to bottom
    const isInitialLoad = previousCount === 0 && currentMessageCount > 0;

    // Scroll when a newer tail message appears (including same-count refreshes)
    const hasNewTailMessage =
      currentMessageCount >= previousCount &&
      currentMessageCount > 0 &&
      `${lastMessageId}` !== `${previousLastMessageId}`;

    if (!loadingMessageId && !loadingOlder && (isInitialLoad || hasNewTailMessage)) {
      const rafId = scheduleBottomScroll();
      previousMessageCountRef.current = currentMessageCount;
      previousLastMessageIdRef.current = lastMessageId ?? null;
      return () => {
        if (rafId != null && typeof window !== 'undefined') {
          window.cancelAnimationFrame(rafId);
        }
      };
    }

    previousMessageCountRef.current = currentMessageCount;
    previousLastMessageIdRef.current = lastMessageId ?? null;
  }, [lastMessageId, loadingMessageId, loadingOlder, messages, scheduleBottomScroll]);

  const hasKeySyncError = keySyncStatus === 'error';
  const keySyncReasonMessage = hasKeySyncError
    ? KEY_SYNC_ERROR_MESSAGE
    : getKeySyncReasonMessage(keySyncReason);
  const keySyncStatusText =
    keySyncStatus === 'requesting'
      ? 'Requesting key sync...'
      : keySyncStatus === 'waiting'
        ? 'Waiting for a synced device...'
        : keySyncStatus === 'hydrating'
          ? 'Syncing key...'
          : hasKeySyncError
            ? KEY_SYNC_ERROR_MESSAGE
            : 'Not synced';

  return (
    <>
      <div className="responder-messenger-thread__messages" ref={scrollRef}>

        {showEncryptionKeySyncBanner && (
          <div
            className={`responder-messenger-key-sync-banner${
              isEncryptionKeySynced ? ' is-synced' : ''
            }`}
          >
            <div className="responder-messenger-key-sync-banner__copy">
              <strong>
                {isEncryptionKeySynced
                  ? 'Encryption keys are synced for this conversation.'
                  : keySyncStatusText}
              </strong>
              {!isEncryptionKeySynced && !hasKeySyncError && <span>{keySyncReasonMessage}</span>}
            </div>
            <div className="responder-messenger-key-sync-banner__actions">
              {isEncryptionKeySynced ? (
                <>
                  <span className="responder-messenger-key-sync-banner__status">Synced</span>
                  {onClearEncryptionKey && (
                    <button
                      type="button"
                      className="btn btn-sm btn-outline-secondary"
                      onClick={onClearEncryptionKey}
                    >
                      Clear key
                    </button>
                  )}
                </>
              ) : (
                <button
                  type="button"
                  className="btn btn-sm btn-primary"
                  onClick={onRequestKeySync}
                  disabled={keySyncStatus === 'requesting' || keySyncStatus === 'hydrating'}

                >
                  Request sync
                </button>
              )}

            </div>
          </div>
        )}
        {loadingOlder && (
          <div className="responder-messenger-thread__loading responder-messenger-thread__loading--top">
            Loading older messages...
          </div>
        )}
        {loadingMessageId && (
          <div className="responder-messenger-thread__loading">Loading message context...</div>
        )}
        {messages.length === 0 ? (
          loading ? (
            <div className="responder-messenger-thread__loading">Loading messages...</div>

          ) : (
            <div className="responder-messenger-empty">
              No messages yet. Start the conversation!
            </div>
          )
        ) : (
          messages.map((message) => {
            if (message.content_type === 'voice-call') {
            }
            return message.content_type === 'voice-call' ? (
              <CallSystemMessage key={message.id} message={message} participants={participants} />
            ) : (
              <MessageBubble
                key={message.id}
                message={message}
                currentUserId={currentUserId}
                participants={participants}
                onDelete={onDeleteMessage}
                onToggleReaction={onToggleReaction}
                onReply={onReply}
                onJumpToMessage={handleJumpToMessage}
                onMediaLoaded={handleMediaLoaded}
              />
            );
          })
        )}
      </div>
    </>
  );
}
